import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, classesBouton, Switch } from '@artosera/ui';
import {
  useFluxDiffusion,
  useEtatsDiffusionOeuvre,
  useChoisirOeuvre,
} from '../../../api/diffusion.js';
import styles from './DiffusionTab.module.css';

export function DiffusionTab({
  galerieSlug,
  oeuvreId,
  peutChoisir,
}: {
  galerieSlug: string;
  oeuvreId: string;
  peutChoisir: boolean;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const flux = useFluxDiffusion(galerieSlug);
  const etats = useEtatsDiffusionOeuvre(galerieSlug, oeuvreId);
  const choisir = useChoisirOeuvre(galerieSlug, oeuvreId);
  return (
    <section className={styles.page} data-ui="artwork-diffusion">
      <p>{t('regie:diffusion.fiche.intro')}</p>
      {flux.isPending || etats.isPending ? <p role="status">{t('commun:chargement')}</p> : null}
      {flux.isError || etats.isError || choisir.isError ? (
        <p role="alert">{t('commun:erreurReseau')}</p>
      ) : null}
      {flux.data?.flux.map((f) => {
        const nom = f.plateforme === 'artsy' ? 'Artsy' : 'Artnet';
        const etat = etats.data?.etats.find((e) => e.plateforme === f.plateforme);
        const label = etat
          ? t(`regie:diffusion.fiche.etats.${etat.etat}`)
          : t('regie:diffusion.fiche.nonSynchronisee');
        const motifs = etat?.erreurs.map((e) => t(`regie:diffusion.motifs.${e.code}`)).join(', ');
        return (
          <article key={f.id + f.plateforme} className={styles.ligne}>
            <div>
              <Switch
                id={`choix-${f.plateforme}-${oeuvreId}`}
                label={t('regie:diffusion.fiche.diffuser', { plateforme: nom })}
                checked={
                  f.toutesLesEligibles || (etats.data?.choisiePar.includes(f.plateforme) ?? false)
                }
                disabled={!peutChoisir || f.toutesLesEligibles || choisir.isPending}
                onCheckedChange={(choisie) => choisir.mutate({ plateforme: f.plateforme, choisie })}
              />
              {f.toutesLesEligibles ? (
                <p className={styles.aide}>{t('regie:diffusion.fiche.toutes')}</p>
              ) : null}
              <p>
                {nom} : {label}
                {motifs ? ` — ${motifs}` : ''}
              </p>
            </div>
            <Badge
              tone={
                etat?.etat === 'diffusee'
                  ? 'available'
                  : etat?.etat === 'refusee'
                    ? 'sold'
                    : 'neutral'
              }
            >
              {t(f.actif ? 'regie:diffusion.ouvert' : 'regie:diffusion.ferme')}
            </Badge>
          </article>
        );
      })}
      {flux.data?.flux.length === 0 ? <p>{t('regie:diffusion.fiche.aucun')}</p> : null}
      <Link className={classesBouton('secondary', 'sm')} to={`/${galerieSlug}/reglages/diffusion`}>
        {t('regie:diffusion.fiche.regler')}
      </Link>
    </section>
  );
}
